import { Injectable } from '@angular/core';
import { BaseTransaction } from './transaction.model';
import { ShareTransaction } from '../../../pages/authenticated/private-equity/state/private-equity-legacy.models';
import { InstrumentsQuery } from '../../../shared/state/instruments/instruments.query';
import { Acct } from '../../../shared/state/accts/acct.model';
import { AccountSelectModel } from '../../../shared/components/accounts-selector/account.select.model';
import { PrivateEquityCompany } from '../../../pages/authenticated/private-equity/state/private-equity.model';

@Injectable({
    providedIn: 'root'
})
export class TransformLegacyService {
    constructor(private instrumentsQuery: InstrumentsQuery) {}

    /** privateEquityTransaction - transform a private equity transaction object to legacy format
     * @param transaction - company object to transform
     * @returns share (legacy) transaction object
     */
    privateEquityTransaction(transaction: BaseTransaction<any>): ShareTransaction {
        const instrument = this.instrumentsQuery.getInstrumentById(
            transaction.instrumentId
        );
        const companyInstrument = this.instrumentsQuery.getInstrumentById(
            instrument.underlyingInstrumentId
        );
        const accounts: AccountSelectModel = this._extractLegacyAccountModel(transaction);
        return {
            ...accounts,
            companyInstrument,
            correlationId: transaction.correlationId,
            // Passing the instrumentId to Backend to identify what underlying instrument
            // of the company we are trying to make a transaction for
            instrumentId: transaction.instrumentId,
            instrumentType: transaction.instrumentType,
            optionalInfo: {
                notes: transaction.notes || null
            },
            price: transaction.price,
            priceCurrency: transaction.currency,
            privateInstrument: true,
            quantity: transaction.quantity,
            settlementDate: transaction.dates.settlementDate,
            shareType: instrument.instrumentData.shareType,
            transactionDateTime: transaction.dates.transactionDate,
            transactionType: transaction.buyOrSell === 'bought' ? 'BUY' : 'SELL'
        };
    }

    private _extractLegacyAccountModel(transaction: any): AccountSelectModel {
        return Object.entries<Acct>(transaction.accounts).reduce((acc, [key, value]) => {
            acc[key] = value
                ? { value: value.accountId, label: value.accountClientAlias }
                : null;
            return acc;
        }, {} as AccountSelectModel);
    }

    privateEquityConvertibleTransaction(transaction: BaseTransaction<any>) {
        const accounts: AccountSelectModel = this._extractLegacyAccountModel(transaction);
        return {
            ...accounts,
            companyInstrumentId: transaction.companyInstrumentId,
            optionalInfo: {
                notes: transaction.notes || null
            },
            privateInstrument: true,
            instrumentType: 'PE_CONVERTIBLE',
            priceCurrency: transaction.currency,
            correlationId: transaction.correlationId,
            transactionType: transaction.buyOrSell === 'bought' ? 'BUY' : 'SELL',
            cap: transaction.cap,
            quantity: transaction.amount,
            couponRate: transaction.interestRate,
            discountRate: transaction.discountRate,
            maturityDate: transaction.dates.settlementDate,
            transactionDateTime: transaction.dates.transactionDate,
            paymentInKind: transaction.paymentInKind
        };
    }

    privateEquityOptionTransaction(transaction: BaseTransaction<any>) {
        const accounts: AccountSelectModel = this._extractLegacyAccountModel(transaction);
        return {
            ...transaction,
            ...accounts,
            transactionType: transaction.buyOrSell === 'bought' ? 'BUY' : 'SELL',
            settlementDate: transaction.dates.settlementDate,
            transactionDateTime: transaction.dates.transactionDate,
            priceCurrency: transaction.currency
        };
    }

    privateEquityShareholderContributionTransaction(transaction: BaseTransaction<any>) {
        const accounts: AccountSelectModel = this._extractLegacyAccountModel(transaction);
        return {
            ...transaction,
            ...accounts,
            transactionType:
                transaction.buyOrSell === 'contributed'
                    ? 'ENTER_AGREEMENT'
                    : 'EXIT_AGREEMENT',
            settlementDate: transaction.dates.settlementDate,
            quantity: transaction.amount,
            transactionDateTime: transaction.dates.transactionDate,
            priceCurrency: transaction.currency
        };
    }

    /** privateEquityCompany - transform a private equity company object to legacy format
     * @param company - company object to transform
     * @returns legacy company object
     */
    privateEquityCompany(company: Partial<PrivateEquityCompany>) {
        return {
            businessDescription: company.description,
            currency: company.currency,
            geography: null,
            listed: false,
            name: company.name,
            orgNumber: company.nationalId,
            privateInstrument: true,
            sector: null,
            country: company?.metaData?.country,
            city: company?.metaData?.stateProvince,
            zipCode: company?.metaData?.postalCode,
            website: company?.website,
            type: 'COMPANY',
            isin: company?.metaData?.isin
        };
    }
}
